import React from "react";
import styles from "./admin.module.scss";
import cx from "./cx";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "dangerSolid"
  | "mint";

const variantClass: Record<ButtonVariant, string | undefined> = {
  primary: undefined,
  secondary: styles.secondary,
  ghost: styles.ghost,
  danger: styles.danger,
  dangerSolid: styles.dangerSolid,
  mint: styles.mint,
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  block?: boolean;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  icon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      block,
      loading,
      icon,
      className,
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={cx(
        styles.btn,
        variantClass[variant],
        size === "sm" && styles.sm,
        size === "lg" && styles.lg,
        block && styles.block,
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : icon}
      {children}
    </button>
  ),
);
Button.displayName = "Button";

export interface IconButtonProps
  extends Omit<ButtonProps, "children" | "icon" | "block"> {
  /** Accessible name; also used as tooltip. */
  label: string;
  icon: React.ReactNode;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { label, icon, size = "md", variant = "ghost", className, ...rest },
    ref,
  ) => (
    <Button
      ref={ref}
      variant={variant}
      className={cx(
        styles.iconBtn,
        size === "sm" && styles.iconBtnSm,
        className,
      )}
      aria-label={label}
      title={label}
      icon={icon}
      {...rest}
    />
  ),
);
IconButton.displayName = "IconButton";

export default Button;
